import { clsx, type ClassValue } from "clsx";
import { Country } from "country-state-city";
import moment from "moment/moment";
import toast from "react-hot-toast";
import {
  BackStoryRole,
  EventQuery,
  EventSupportCurrencyProvider,
} from "src/types/api.d";
import { twMerge } from "tailwind-merge";
import { getEffectiveTimezone } from "./timezone";

export const phoneRegExp =
  /^\+((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:\W*\d){0,13}\d$/;

// export function getUtcOffset(date: Date) {
//   return moment(date).add(moment(date).utcOffset(), "minutes").utc();
// }

export function getUtcOffset(date: Date) {
  // Convert the date to UTC directly without adding the offset again
  return moment(date).utc().local();
}

export function convertISOTimeToTime(
  isoString: string,
  timezone?: string | null
) {
  const date = new Date(isoString);
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: getEffectiveTimezone(timezone),
  };
  return new Intl.DateTimeFormat("en-US", options).format(date);
}

export function formatDate(inputDate: string, timezone?: string | null) {
  const date = new Date(inputDate);
  const options = {
    weekday: "long" as "long",
    month: "short" as "short",
    day: "2-digit" as "2-digit",
    year: "numeric" as "numeric",
    timeZone: getEffectiveTimezone(timezone),
  };
  return new Intl.DateTimeFormat("en-US", options).format(date);
}

export function indexOfEarliestDate(arr: Date[]): number {
  if (arr.length === 0) {
    return -1;
  }

  var earliest = arr[0];
  var earliestIndex = 0;

  for (var i = 1; i < arr.length; i++) {
    if (moment(arr[i]).isBefore(earliest)) {
      earliestIndex = i;
      earliest = arr[i];
    }
  }

  return earliestIndex;
}

export function indexOfLatestDate(arr: Date[]): number {
  if (arr.length === 0) {
    return -1;
  }

  let latest = arr[0];
  let latestIndex = 0;

  for (let i = 1; i < arr.length; i++) {
    if (moment(arr[i]).isAfter(latest)) {
      latestIndex = i;
      latest = arr[i];
    }
  }

  return latestIndex;
}

// a more concise version of the initial
export const indexOfEarliestDate2 = (arr: Date[]): number =>
  arr.reduce(
    (bestIndexSoFar, currentlyTestedValue, currentlyTestedIndex, array) =>
      moment(currentlyTestedValue).isBefore(array[bestIndexSoFar])
        ? currentlyTestedIndex
        : bestIndexSoFar,
    0
  );

export const copyToClipboard = (text: string, alert: any) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success(alert);
    })
    .catch(() => {
      toast.error(`An Error occurred while copying`);
    });
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.charAt(4) === "0" && phoneNumber.slice(1, 4) === "234") {
    return `+234${phoneNumber.slice(5)}`;
  } else {
    return `${phoneNumber}`;
  }
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface CurrencyProviderInfo {
  hasBankDetails: boolean;
  onlyBankDetails: boolean;
  hasStripe: boolean;
  hasFlutterwave: boolean;
  hasPaystack: boolean;
  hasMonnify: boolean;
}

/**
 * Builds a map of currency to provider information from supported currencies
 * @param supportedCurrencies Array of supported currencies with their providers
 * @returns Map of currency (lowercase) to provider information
 */
export function buildCurrencyProviderMap(
  supportedCurrencies:
    | NonNullable<EventQuery["event"]>["supported_currencies"]
    | undefined
): Map<string, CurrencyProviderInfo> {
  const map = new Map<string, CurrencyProviderInfo>();

  if (!supportedCurrencies) {
    return map;
  }

  supportedCurrencies.forEach((supportedCurrency) => {
    if (supportedCurrency?.currency) {
      const currency = supportedCurrency.currency.toLowerCase();
      const providers = supportedCurrency.providers?.filter(Boolean) || [];
      const hasBankDetails = providers.includes(
        EventSupportCurrencyProvider.BankDetails
      );
      const hasStripe = providers.includes(EventSupportCurrencyProvider.Stripe);
      const hasFlutterwave = providers.includes(
        EventSupportCurrencyProvider.Flutterwave
      );
      const hasPaystack = providers.includes(
        EventSupportCurrencyProvider.Paystack
      );
      const hasMonnify = providers.includes(
        EventSupportCurrencyProvider.Monnify
      );
      // Only bank details if it has BANK_DETAILS and no other providers
      const onlyBankDetails = hasBankDetails && providers.length === 1;

      map.set(currency, {
        hasBankDetails,
        onlyBankDetails,
        hasStripe,
        hasFlutterwave,
        hasPaystack,
        hasMonnify,
      });
    }
  });

  return map;
}

export const getCountryIsoAlpha2FromCurrency = (currency: string) => {
  return Country.getAllCountries().find(
    (country) => country.currency === currency.toUpperCase()
  )?.isoCode;
};

export const getBackstoryRole = (role: BackStoryRole | undefined) => {
  if (role === "Both") {
    return "The Couple";
  }
  return role;
};

export const FREE_MEDIA_LIMIT = 15;
export const LUXE_GATE_DATE = new Date("2026-08-12T00:00:00.000Z");
